import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, Observable } from 'rxjs';
import { USER_SURVEY_STATUS } from '../enums/user-survey-status.enum';
import { SurveySummary } from '../interfaces/survey-summary.interface';
import { Survey } from '../interfaces/survey.interface';
import { UserSurveyResponse } from '../interfaces/user-survey-response.interface';
import { UserSurveyStatus, UserSurveyStatusDto } from '../interfaces/user-survey-status.interface';
import { AumicoCoreProvider } from '../provider/aumico.core.provider';
import { RoutesUtil } from '../utils/routes.util';

@Injectable({
    providedIn: 'root',
})
export class SurveysService {
    constructor(
        private readonly http: HttpClient,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly routesUtil: RoutesUtil,
    ) {}

    public createSurvey$(): Observable<Survey> {
        const url = this.routesUtil.createSurvey();
        this.coreProvider.dispatchBlockingRequest('createSurvey$');
        return this.http
            .post<Survey>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createSurvey$')));
    }

    public stopSurvey$(id: string): Observable<Survey> {
        const url = this.routesUtil.stopSurveyRoute(id);
        this.coreProvider.dispatchBlockingRequest('stopSurvey$');
        return this.http
            .put<Survey>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('stopSurvey$')));
    }

    public downloadSurveyCsv$(id: string): Observable<any> {
        const url = this.routesUtil.downloadSurveyRoute(id);
        this.coreProvider.dispatchBlockingRequest('downloadSurveyCsv$');
        return this.http
            .get(url, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('downloadSurveyCsv$')));
    }

    public getActiveSurvey$(): Observable<Survey> {
        const url = this.routesUtil.getActiveSurveyRoute();
        return this.http.get<Survey>(url, {});
    }

    public getSurveyById$(id: string): Observable<Survey> {
        const url = this.routesUtil.getSurveyByIdRoute(id);
        this.coreProvider.dispatchBlockingRequest('getSurveyById$');
        return this.http
            .get<Survey>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getSurveyById$')));
    }

    public getSurveyResponses$(surveyId: string): Observable<UserSurveyResponse[]> {
        const url = this.routesUtil.getSurveyResponsesRoute(surveyId);
        this.coreProvider.dispatchBlockingRequest('getSurveyResponses$');
        return this.http
            .get<UserSurveyResponse[]>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getSurveyResponses$')));
    }

    public getAllSurveys$(): Observable<SurveySummary[]> {
        const url = this.routesUtil.getAllSurveysRoute();
        this.coreProvider.dispatchBlockingRequest('getAllSurveys$');
        return this.http
            .get<SurveySummary[]>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAllSurveys$')));
    }

    public checkUserEligibility$(id: string): Observable<boolean> {
        const url = this.routesUtil.getCheckUserEligibilityRoute(id);
        this.coreProvider.dispatchBlockingRequest('checkUserEligibility$');
        return this.http
            .get<boolean>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('checkUserEligibility$')));
    }

    public saveResponse$(surveyId: string, questionNumber: number, response: string): Observable<UserSurveyResponse> {
        const url = this.routesUtil.getSaveResponseRoute(surveyId);
        return this.http.post<UserSurveyResponse>(url, { questionNumber, response });
    }

    public updateStatus$(id: string, status: USER_SURVEY_STATUS): Observable<UserSurveyStatus> {
        const url = this.routesUtil.getUpdateUserSurveyStatusRoute(id);
        this.coreProvider.dispatchBlockingRequest('updateStatus$');
        return this.http
            .put<UserSurveyStatus>(url, { status })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateStatus$')));
    }

    public getUserSurveyStatus$(id: string): Observable<UserSurveyStatusDto> {
        const url = this.routesUtil.getGetUserSurveyStatusRoute(id);
        return this.http.get<UserSurveyStatusDto>(url);
    }

    public getUsersToNotify$(id: string): Observable<string[]> {
        const url = this.routesUtil.getGetUsersToNotifyRoute(id);
        return this.http.get<string[]>(url, {});
    }

    public getEligibleUsersSurveyStatus$(id: string): Observable<UserSurveyStatus[]> {
        const url = this.routesUtil.getEligibleUsersSurveyStatus(id);
        return this.http.get<UserSurveyStatus[]>(url, {});
    }

    public markUserAsNotified$(id: string): Observable<UserSurveyStatus> {
        const url = this.routesUtil.getMarkUserAsNotifiedRoute(id);
        return this.http.post<UserSurveyStatus>(url, {});
    }
}
